import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { observationService } from '../../services/observationService';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Filter,
  Beaker
} from 'lucide-react';

const Observation = () => {
  const [observations, setObservations] = useState([]);

  // Load observations from API only
  const loadObservations = async () => {
    try {
      const response = await observationService.getObservations();
      if (response && response.success && response.data) {
        const data = response.data;
        // Sort by ID descending to show newest items first
        const sortedData = Array.isArray(data) ? data.sort((a, b) => (b.id || 0) - (a.id || 0)) : data;
        setObservations(sortedData);
      } else {
        throw new Error(response?.message || 'Failed to load observations');
      }
    } catch (error) {
      console.warn('Failed to load observations from API:', error.message);
      setObservations([]);
    }
  };

  useEffect(() => {
    loadObservations();
  }, []);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingObservation, setEditingObservation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, reset, formState: { errors }, setValue } = useForm();

  // Auto-generate code
  const generateCode = () => {
    if (!observations || observations.length === 0) return 'OB001';
    const maxCode = Math.max(...observations.map(obs => parseInt(obs?.code?.replace('OB', '') || '0')), 0);
    const nextNumber = maxCode + 1;
    return `OB${nextNumber.toString().padStart(3, '0')}`;
  };

  // Observation types for filter dropdown (kept only for UI; no seeded rows)
  const observationTypes = [
    'Plant Height',
    'Germination %',
    'Population',
    'Vigour',
    'Germination DAS',
    'Plant height',
    'Plant width',
    'No. of branches'
  ];

  const filteredObservations = observations.filter(observation => {
    const matchesSearch = (
      (observation.code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (observation.observation_type || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (observation.status || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    const matchesStatus = filterStatus === 'all' || observation.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const handleAdd = async (data) => {
    setIsLoading(true);
    try {
      const observationData = {
        code: generateCode(), // Auto-generate code
        observationType: data.observation_type,
        status: data.status || 'active'
      };

      const response = await observationService.createObservation(observationData);
      if (response && response.success) {
        await loadObservations();
        toast.success('Observation added successfully!');
        setShowAddModal(false);
        reset();
      } else {
        throw new Error(response?.message || 'Failed to add observation');
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error(error.message || 'Failed to add observation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async (data) => {
    setIsLoading(true);
    try {
      const observationData = {
        code: editingObservation.code, // Keep existing code, don't allow editing
        observationType: data.observation_type,
        status: data.status
      };

      const response = await observationService.updateObservation(editingObservation.id, observationData);
      if (response && response.success) {
        await loadObservations();
        toast.success('Observation updated successfully!');
        setShowAddModal(false);
        setEditingObservation(null);
        reset();
      } else {
        throw new Error(response?.message || 'Failed to update observation');
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error(error.message || 'Failed to update observation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (observationId) => {
    if (!window.confirm('Are you sure you want to delete this observation?')) return;

    setIsLoading(true);
    try {
      const response = await observationService.deleteObservation(observationId);
      if (response && response.success) {
        await loadObservations();
        toast.success('Observation deleted successfully!');
      } else {
        throw new Error(response?.message || 'Failed to delete observation');
      }
    } catch (error) {
      console.error('API error:', error);
      toast.error(error.message || 'Failed to delete observation');
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (observation) => {
    setEditingObservation(observation);
    setValue('code', observation.code);
    setValue('observation_type', observation.observation_type);
    setValue('status', observation.status);
    setShowAddModal(true);
  };

  const getStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid Date';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Observation Management</h1>
          <p className="text-gray-600">Manage observation types for field trials</p>
        </div>
        <button 
          onClick={() => {
            setEditingObservation(null);
            reset();
            // Pre-fill with auto-generated code
            setValue('code', generateCode());
            setShowAddModal(true);
          }}
          className="btn-primary flex items-center mt-4 sm:mt-0"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Observation
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search observations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Observations Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Observation Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredObservations.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                    No observations found
                  </td>
                </tr>
              ) : (
                filteredObservations.map((observation) => (
                  <tr key={observation.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      {observation.code}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {observation.observation_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(observation.status)}`}>
                        {observation.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openEditModal(observation)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit Observation"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(observation.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete Observation"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {filteredObservations.length === 0 && (
          <div className="text-center py-10">
            <Beaker className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No observations found.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-700">
          Showing 1 to {filteredObservations.length} of {filteredObservations.length} results
        </div>
        <div className="flex items-center space-x-2">
          <button
            disabled
            className="px-3 py-1 text-sm border border-gray-300 rounded text-gray-400 cursor-not-allowed"
          >
            Previous
          </button>
          <span className="px-3 py-1 text-sm text-gray-700">
            Page 1 of 1
          </span>
          <button
            disabled
            className="px-3 py-1 text-sm border border-gray-300 rounded text-gray-400 cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {editingObservation ? 'Edit' : 'Add'} Observation
              </h2>
              <form onSubmit={handleSubmit(editingObservation ? handleEdit : handleAdd)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                  <input
                    type="text"
                    {...register('code', { required: 'Code is required' })}
                    className="input-field bg-gray-100 cursor-not-allowed font-mono"
                    placeholder={editingObservation ? "Code cannot be edited" : "Code will be generated automatically"}
                    readOnly
                  />
                  {errors.code && <p className="text-red-500 text-sm mt-1">{errors.code.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observation Type</label>
                  <input
                    type="text"
                    {...register('observation_type', { required: 'Observation type is required' })}
                    className="input-field"
                    placeholder="Enter observation type"
                  />
                  {errors.observation_type && <p className="text-red-500 text-sm mt-1">{errors.observation_type.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    {...register('status', { required: 'Status is required' })}
                    className="input-field"
                  >
                    <option value="">Select Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  {errors.status && <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingObservation(null);
                      reset();
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary"
                  >
                    {isLoading ? 'Saving...' : (editingObservation ? 'Update' : 'Add')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Observation; 